import { Directive, inject, Input, OnDestroy, OnInit, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslationService } from 'app/core/internationalization/translation.service';

@Directive({
  selector: '[translateWithFormat]'
})
export class TranslateWithFormatDirective implements OnInit, OnDestroy {
  private vcr = inject(ViewContainerRef);
  private renderer = inject(Renderer2);
  private _translationService = inject(TranslationService);

  @Input('translateWithFormat') public key!: string;
  @Input() public templateParams: Record<string, TemplateRef<any>> = {};

  private destroy$ = new Subject<void>();

  public ngOnInit(): void {
    this._translationService.translationsReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._render();
      });
  }

  /**
   * Renders the translated string, replacing {placeholder} tokens with the
   * corresponding ng-template from `templateParams` and inserting plain text
   * for everything else.
   *
   * Because we need to interleave text nodes with embedded-view nodes in a
   * specific order, Angular's ViewContainerRef alone can't manage the layout.
   * Embedded-view root nodes are moved into the host element manually, so
   * vcr.clear() cannot remove them — we must clear the host element ourselves.
   */
  private _render(): void {
    const placeholderParams = Object.keys(this.templateParams).reduce<Record<string, string>>((acc, key) => {
      acc[key] = `{${key}}`;
      return acc;
    }, {});

    const translated = this._translationService.instant(this.key, placeholderParams);
    if (!translated || translated === this.key) { return; }

    this.vcr.clear();

    const hostEl: HTMLElement = this.vcr.element.nativeElement;
    this.renderer.setProperty(hostEl, 'textContent', '');

    const parts = translated.split(/({\w+})/g);

    parts.forEach((part) => {
      const match = part.match(/{(\w+)}/);
      if (match) {
        const templateRef = this.templateParams[match[1]];
        if (templateRef) {
          const view = this.vcr.createEmbeddedView(templateRef);
          view.rootNodes.forEach((node) => { this.renderer.appendChild(hostEl, node); });
        }
        else {
          this.renderer.appendChild(hostEl, this.renderer.createText(part));
        }
      }
      else {
        this.renderer.appendChild(hostEl, this.renderer.createText(part));
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
